import React, {useCallback, useMemo, useRef} from 'react';
import Joyride, {Step, CallBackProps, STATUS, StoreHelpers} from 'react-joyride';
import {useCookieValue} from '@react-hookz/web/esm/useCookieValue';
import {JoyRideTooltip} from 'Components/JoyRideTooltip';
import {tutorialCopy, uiTutorialCopy} from './tutorialCopy';
import {useProductContext} from 'Contexts/ProductContext';
import {sendSlackAlert} from '../../../slack';
import {useUserContext} from 'Contexts/UserContext/UserContext';

interface Props {}

export const RenditionTutorial: React.FC<Props> = () => {
    const helpers = useRef<StoreHelpers>();

    const getHelpers = useCallback((h: StoreHelpers) => {
        helpers.current = h;
    }, []);

    const [hasFinishedTutorial, setHasFinishedTutorial] = useCookieValue('rendition-tutorial');

    const {user, userId} = useUserContext();
    const {mode} = useProductContext();

    const handleIntroCallback = useCallback(
        (data: CallBackProps) => {
            const {status, index} = data;
            const options: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

            if (options.includes(status)) {
                setHasFinishedTutorial('true');
            }
            if (status === STATUS.SKIPPED) {
                sendSlackAlert(userId, user, 'Rendition Tutorial Skipped From Step ' + (index + 1), 'tutorial-flow');
            }
            if (status === STATUS.FINISHED) {
                sendSlackAlert(userId, user, 'Rendition Tutorial Completed', 'tutorial-flow');
            }
        },
        [setHasFinishedTutorial, userId, user]
    );

    const finishTutorial = useCallback(() => {
        setHasFinishedTutorial('true');
        helpers.current?.reset(true);
        sendSlackAlert(userId, user, 'Rendition Tutorial Completed', 'tutorial-flow');
    }, [setHasFinishedTutorial, userId, user]);

    const STEPS: Step[] = useMemo(
        () =>
            [
                {
                    content: tutorialCopy.intro,
                    placement: 'center',
                    title: 'Welcome to the Rendition Builder!',
                    target: 'body',
                },
                {
                    content: tutorialCopy.grouping,
                    placement: 'center',
                    title: 'Most Important: Grouping',
                    target: 'body',
                },
                {
                    content: tutorialCopy.ungrouping,
                    placement: 'center',
                    title: 'Need to ungroup?',
                    target: 'body',
                },
                // {
                //     content: tutorialCopy.restructuring,
                //     placement: 'center',
                //     title: 'Move Layers & Absolute Position',
                //     target: 'body',
                // },
                {
                    content: tutorialCopy.sizing,
                    placement: 'center',
                    title: "Size em' up",
                    target: 'body',
                },
                {
                    content: uiTutorialCopy.shortcuts,
                    target: '#shortcuts-popover',
                    title: 'Shortcuts',
                },
                {
                    content: uiTutorialCopy.layers,
                    target: '#rendition-layers',
                    title: 'Layers',
                },
                {
                    content: uiTutorialCopy.screenSize,
                    target: '#rendition-screen-size',
                    title: 'Resizing',
                },
                {
                    content: uiTutorialCopy.rightSidebar,
                    target: '#builder-right-rail',
                    title: 'Get Editing',
                    placement: 'left',
                },
                {
                    content: uiTutorialCopy.codeSandbox,
                    target: '#rendition-sandbox-btn',
                    title: 'Get Your Code!',
                },
                mode === 'Rendition'
                    ? {
                          content: uiTutorialCopy.copyForWebflow,
                          target: '#rendition-copy-webflow-btn',
                          title: 'Webflow Magic!',
                      }
                    : {
                          content: uiTutorialCopy.copyForTailwind,
                          target: '#rendition-copy-tailwind',
                          title: 'Tailwind Magic!',
                      },
                mode === 'Rendition' && {
                    content: uiTutorialCopy.downloadAssets,
                    target: '#rendition-download-assets-btn',
                    title: 'Download Your Assets',
                },
                {
                    content: tutorialCopy.metrics(userId, user, finishTutorial),
                    target: 'body',
                    title: 'Help Us Help You',
                    placement: 'center',
                },
            ].filter(Boolean) as Step[],
        [finishTutorial, mode, user, userId]
    );

    return (
        <Joyride
            getHelpers={getHelpers}
            run={hasFinishedTutorial !== 'true'}
            callback={handleIntroCallback}
            beaconComponent={() => null}
            continuous
            steps={STEPS}
            spotlightPadding={8}
            floaterProps={{hideArrow: true}}
            disableScrolling
            disableScrollParentFix
            styles={{
                options: {
                    arrowColor: 'transparent',
                    zIndex: 999,
                },
            }}
            disableOverlayClose
            tooltipComponent={JoyRideTooltip}
        />
    );
};
